import { PrinterIcon } from '@heroicons/react/20/solid';
import { Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { IconButton, PrimaryButton, SecondaryButton } from '../../../../components/buttons';
import { DemoCTAModal, useDemoCTA } from '../../../../components/demo-cta-modal';
import { Select } from '../../../../components/forms';
import { PrintContainer } from '../../../../components/print-container';
import { PageToolbar } from '../../../../components/toolbar/page-toolbar';
import type { Order } from '../../../../data/order';
import { StoreOperation, unavailableReason } from '../../../../data/permissions';
import { isDisabledByBounce, lastBounceReason } from '../../../../data/representative';
import type { Store } from '../../../../data/store';
import type { Vendor } from '../../../../data/vendor';
import { multiColumnSort } from '../../../../helpers/column-sort';
import { formatShortDate } from '../../../../helpers/format-date';
import { SendMethodLabels } from '../../../../helpers/send-method-labels';
import { useSubmitOrder } from '../../../../hooks/orders';
import { PurchaseOrder } from '../../purchase-order';
import {
  itemHasCount,
  itemHasUserCount,
  type ProductOrderItem,
  simplifyOrderItem,
} from '../data/product-order-item';
import { Divider } from './divider';
import { OptionWrapper } from './option-wrapper';

type SkippedRecipient =
  | { kind: 'bounced'; email: string; at: Date | null; reason: string }
  | { kind: 'send_order_off'; email: string };

function computeRecipients(
  store: Store,
  vendor: Vendor,
  method: string
): { sendingTo: string[]; notSendingTo: SkippedRecipient[] } {
  const storeEmails = store.users.map((u) => u.email.toLowerCase());
  if (method !== 'email') {
    return { sendingTo: [...new Set(storeEmails)], notSendingTo: [] };
  }

  const sendingToReps: string[] = [];
  const notSendingTo: SkippedRecipient[] = [];
  for (const rep of vendor.order_settings.representatives) {
    const email = rep.email.toLowerCase();
    if (!rep.send_order) {
      notSendingTo.push({ kind: 'send_order_off', email });
      continue;
    }

    if (isDisabledByBounce(rep)) {
      notSendingTo.push({
        kind: 'bounced',
        email,
        at: rep.last_permanent_bounce ?? null,
        reason: lastBounceReason(rep),
      });
      continue;
    }

    sendingToReps.push(email);
  }

  return {
    sendingTo: [...new Set([...storeEmails, ...sendingToReps])],
    notSendingTo,
  };
}

function ConfirmationRecipients({
  store,
  vendor,
  method,
  onNoOneToSendToChange,
}: {
  store: Store;
  vendor: Vendor;
  method: string;
  onNoOneToSendToChange: (empty: boolean) => void;
}) {
  const { sendingTo, notSendingTo } = useMemo(
    () => computeRecipients(store, vendor, method),
    [store, vendor, method]
  );

  useEffect(() => {
    onNoOneToSendToChange(sendingTo.length === 0);
  }, [sendingTo, onNoOneToSendToChange]);

  if (sendingTo.length === 0 && notSendingTo.length === 0) {
    return null;
  }

  // 'email' delivers the order itself to recipients; other methods send
  // the order to the vendor or to disk and only email a confirmation copy.
  const subject = method === 'email' ? 'Order' : 'Confirmation';

  return (
    <div className="space-y-5">
      {sendingTo.length > 0 && (
        <section>
          <h4 className="mb-2 text-sm font-semibold text-gray-900">Sending {subject} To</h4>
          <ul className="space-y-1 font-mono text-sm text-gray-900">
            {sendingTo.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </section>
      )}

      {notSendingTo.length > 0 && (
        <section>
          <h4 className="mb-2 text-sm font-semibold text-status-attention">
            NOT Sending {subject} To
          </h4>
          <ul className="space-y-3">
            {notSendingTo.map((s) => (
              <li key={`${s.email}-${s.kind}`}>
                <RenderSkipped item={s} store={store} vendor={vendor} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {sendingTo.length === 0 && (
        <div className="rounded bg-status-danger-soft p-3 text-sm text-status-danger">
          <p className="font-semibold">No one will receive this order.</p>
          <p>
            All recipients are disabled.{' '}
            <Link
              to="/stores/$storeId/vendors/$vendorId/edit"
              params={{ storeId: store.id, vendorId: vendor.vendor_id }}
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Update the vendor
            </Link>{' '}
            and try again.
          </p>
        </div>
      )}
    </div>
  );
}

function RenderSkipped({
  item,
  store,
  vendor,
}: {
  item: SkippedRecipient;
  store: Store;
  vendor: Vendor;
}) {
  const meta =
    item.kind === 'bounced' ? (
      <>
        {item.reason}
        {item.at ? ` · bounced ${formatShortDate(item.at)}` : ''} ·{' '}
        <Link
          to="/stores/$storeId/vendors/$vendorId/edit"
          params={{ storeId: store.id, vendorId: vendor.vendor_id }}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          Update vendor
        </Link>
      </>
    ) : (
      <>Send Order is turned off for this representative</>
    );

  return (
    <div>
      <div className="font-mono text-sm text-gray-900">{item.email}</div>
      <div className="text-sm text-gray-500">{meta}</div>
    </div>
  );
}

type ReviewOrderOptions = {
  orderItems: ProductOrderItem[];
  methods: string[];
  store: Store;
  vendor: Vendor;
  order: Order;
};

type ReviewOrderResult = {
  body: React.ReactNode;
  footer: React.ReactNode;
};

export function useReviewOrder({
  store,
  vendor,
  order,
  orderItems,
  methods,
}: ReviewOrderOptions): ReviewOrderResult {
  const navigate = useNavigate();
  const { isDemo, showCTA, ctaProps } = useDemoCTA(store);

  const [method, setMethod] = useState(vendor.order_settings.defaults.send_method || 'email');

  // Sort order for Purchase Order display: Department (desc), Brand, Description, Size
  const compareFunction = useMemo(
    () =>
      multiColumnSort<ProductOrderItem>([
        { accessor: (oi) => oi.product.department, direction: 'desc' },
        { accessor: (oi) => oi.product.brand, direction: 'asc' },
        { accessor: (oi) => oi.product.description, direction: 'asc' },
        { accessor: (oi) => oi.product.size_ounces, direction: 'asc' },
      ]),
    []
  );

  // Submit items with suggested quantities, or user-defined quantities (including zero-ed items)
  const selectedOrderItems = useMemo(() => {
    return orderItems
      .filter((oi) => itemHasCount(oi) || itemHasUserCount(oi))
      .sort(compareFunction);
  }, [orderItems, compareFunction]);

  // Only display items with non-zero quantities
  const displayOrderItems = useMemo(() => {
    return selectedOrderItems.filter(itemHasCount).sort(compareFunction);
  }, [selectedOrderItems, compareFunction]);

  const submitOrder = useSubmitOrder();
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [noOneToSendTo, setNoOneToSendTo] = useState(false);
  const submitOrderWithMethod = useCallback(
    (method: string) => {
      setSubmitting(true);
      const updatedOrder = {
        ...order,
        items: selectedOrderItems.map(simplifyOrderItem),
      };

      submitOrder(store.id, updatedOrder, method)
        .then((success) => {
          if (success) {
            navigate({
              to: '/stores/$storeId/orders/$orderId/summary',
              params: { storeId: store.id, orderId: order.id },
              state: { justSubmitted: true },
            });
          }
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
    [order, selectedOrderItems, store, submitOrder, navigate]
  );

  const [printing, setPrinting] = useState(false);

  const sendUnavailableReason = useMemo(() => {
    if (displayOrderItems.length === 0) {
      return 'The order has no products';
    }

    // Sending is owner/manager only on the API side. Disabled with the reason
    // rather than hidden, because the rest of this modal — the purchase order
    // itself, and printing it — is something an employee may legitimately do,
    // and is often exactly what they open it for.
    //
    // Skipped on a demo store, where Submit opens the sign-up prompt and never
    // reaches the API at all.
    if (isDemo) {
      return null;
    }

    return unavailableReason(store, StoreOperation.SendOrder);
  }, [displayOrderItems, isDemo, store]);

  // Electronic ordering names every file the vendor receives after the account
  // number, so an order sent without one is unroutable on their side and is
  // reported back only as a timeout, ~12 minutes later. Block it here and point
  // at the fix instead.
  const missingAccountNumber = method === 'ftp' && vendor.account_number.trim() === '';

  const body = (
    <>
      <DemoCTAModal {...ctaProps} />
      {!confirming && (
        <div>
          <PageToolbar
            left={
              <OptionWrapper label="Send Method">
                <Select<string>
                  value={method}
                  onChange={setMethod}
                  options={methods.map((m) => ({ value: m, label: SendMethodLabels[m] || m }))}
                  className="w-48"
                />
              </OptionWrapper>
            }
            right={
              <>
                <PrimaryButton
                  onClick={() => (isDemo ? showCTA() : setConfirming(true))}
                  disabledReason={sendUnavailableReason}
                >
                  Submit Order
                </PrimaryButton>
                <IconButton tooltip="Print purchase order" onClick={() => setPrinting(true)}>
                  <PrinterIcon className="size-5" aria-hidden="true" />
                </IconButton>
              </>
            }
          />
          <Divider />
          <PurchaseOrder
            store={store}
            vendor={vendor}
            order={order}
            orderItems={displayOrderItems}
          />
          {printing && (
            <PrintContainer onAfterPrint={() => setPrinting(false)}>
              <PurchaseOrder
                store={store}
                vendor={vendor}
                order={order}
                orderItems={displayOrderItems}
              />
            </PrintContainer>
          )}
        </div>
      )}
      {confirming && (
        <div className="space-y-5">
          <div className="space-y-2">
            <p className="text-base text-gray-900">
              Send this <span className="font-semibold">{vendor.name}</span> order via{' '}
              <span className="font-semibold">{SendMethodLabels[method] || method}</span>?
            </p>
            <p className="text-sm text-gray-500">
              You won't be able to modify the order after submission.
            </p>
            {method === 'ftp' && !missingAccountNumber && (
              <p className="text-sm text-gray-500">
                Your order will be submitted to the vendor electronically. A confirmation will also
                be emailed to you for your records.
              </p>
            )}
            {method === 'download' && (
              <p className="text-sm text-gray-500">
                Your order file(s) will be saved to your computer for you to bulk-add on{' '}
                <span className="font-semibold">{vendor.name}</span>'s website. A copy will also be
                emailed to you for your records.
              </p>
            )}
          </div>
          {missingAccountNumber && (
            <div className="rounded bg-status-danger-soft p-3 text-sm text-status-danger">
              <p className="font-semibold">No account number for {vendor.name}.</p>
              <p>
                {vendor.name} can't accept an electronic order without your account number. Set it
                on this vendor in LiquorPOS and upload again, then send this order.
              </p>
            </div>
          )}
          <ConfirmationRecipients
            store={store}
            vendor={vendor}
            method={method}
            onNoOneToSendToChange={setNoOneToSendTo}
          />
        </div>
      )}
    </>
  );

  const footer = confirming ? (
    <>
      <SecondaryButton onClick={() => setConfirming(false)} disabled={submitting}>
        Go Back
      </SecondaryButton>
      <PrimaryButton
        onClick={() => submitOrderWithMethod(method)}
        disabled={submitting || noOneToSendTo || missingAccountNumber}
      >
        {submitting ? 'Sending…' : 'Send'}
      </PrimaryButton>
    </>
  ) : (
    <PrimaryButton
      onClick={() => (isDemo ? showCTA() : setConfirming(true))}
      disabledReason={sendUnavailableReason}
    >
      Submit Order
    </PrimaryButton>
  );

  return { body, footer };
}
